import { isolatePhrase, pluralize, renderVariable } from "@climatepartner/utils";
import { ReactElement } from "react";

export const emissionFactorsLibraryLabels = {
  pageHeadline: "Emission Factors Library",
  pageSubheader: "Your central space to store and manage custom emission factors from licensed databases or suppliers.",
  breadcrumb: "Emission Factors Library",
  tableHeading: "Emission Factors",
  columns: {
    efName: "Name",
    gwp: "GWP",
    unit: "Unit",
    year: "Year",
    region: "Region",
    systemBoundary: "System boundary",
    sourceName: "Source",
    isSupplierSpecific: "Supplier-specific",
  },
  addCta: "Add emission factor",
  addManually: {
    title: "Enter manually",
    description: "Enter the emission factor details yourself.",
  },
  uploadFile: {
    title: "Upload file",
    description: "Add many emission factors at once from a spreadsheet.",
    skippedDuplicates: (count: number): ReactElement =>
      isolatePhrase(
        <>
          Skipped {pluralize(count, "duplicate", "duplicates")}: identical emission factors already in your library or
          repeated in the file.
        </>
      ),
  },
  uploadReport: {
    title: "Upload PCF report",
    description: "Extract an emission factor automatically from a PCF report.",
  },
  lockedTooltip: {
    inUse: "This Emission Factor cannot be edited or deleted because it has already been used in a calculation.",
    rootOnly: "Only root account users can edit or delete Emission Factors.",
  },
  deleteDialog: {
    title: "Delete emission factor?",
    message: (efName: string): ReactElement => (
      <>This removes the emission factor {renderVariable(efName)} from your library. This can't be undone.</>
    ),
    inUseNote: "Emission factors that have been used in a calculation can't be deleted.",
    confirm: "Yes, delete",
    cancel: "Cancel",
    deleting: "Deleting...",
  },
  bulkDelete: {
    action: "Delete selected",
    title: "Delete emission factors?",
    message: (count: number): ReactElement =>
      isolatePhrase(
        <>
          This removes {pluralize(count, "emission factor", "emission factors")} from your library. This can&apos;t be
          undone.
        </>
      ),
    skippedInUseToast: (count: number): ReactElement =>
      isolatePhrase(
        <>Skipped {pluralize(count, "emission factor", "emission factors")} that have been used in a calculation.</>
      ),
  },
  deleteInUseToast: "This emission factor has been used in a calculation and can't be deleted.",
  deleteUnverifiedToast: "Couldn't check whether this emission factor is in use. Please try again.",
  deleteErrorToast: "Failed to delete emission factor",
  updateInUseToast: "This emission factor has been used in a calculation and can't be edited.",
  updateUnverifiedToast: "Couldn't check whether this emission factor is in use. Please try again.",
  updateErrorToast: "Failed to update emission factor",
  emptyState: {
    title: "Build your emission factors library",
    description:
      "Store, reuse, and improve emission factors across all your calculations. Once added, they're available to be applied to your PCF and CCF calculations — no repeated mapping.",
  },
  drafts: {
    sectionHeading: "Drafts",
    productHeader: "Product",
    statusHeader: "Status",
    createdHeader: "Created",
    actionsHeader: "Actions",
    discardAction: "Discard draft",
  },
  form: {
    headerTitle: "Add emission factor",
    editHeaderTitle: "Edit emission factor",
    viewHeaderTitle: "Emission factor",
    requiredFieldsNote: "Fields marked * are required.",
    headerDescription:
      "Fill in the required fields to add an emission factor. Optional fields improve your data quality and may be required for standards like SBTi or PACT — provide as much as you can.",
    editHeaderDescription:
      "Update the emission factor details. Optional fields improve your data quality and may be required for standards like SBTi or PACT — provide as much as you can.",
    coreSectionTitle: "Core emission factor data",
    efNameLabel: "Name",
    efNamePlaceholder: "e.g. Recycled aluminium ingot",
    gwpLabel: "GWP value (kg CO₂e)",
    unitLabel: "Unit",
    yearLabel: "Reference year",
    regionLabel: "Country or region of origin",
    systemBoundaryLabel: "System boundary",
    flagSectionTitle: "FLAG (optional)",
    flagSectionDescription: "Required only when reporting under FLAG-eligible categories.",
    flagNotApplicableLabel: "FLAG not applicable",
    biogenicSectionTitle: "Biogenic emissions (optional)",
    biogenicSectionDescription: "Emissions from biomass, reported separately from fossil emissions.",
    biogenicEmissionsLabel: "Biogenic CO₂ (kg) (optional)",
    landManagementCo2EmissionsLabel: "Land mgmt CO₂",
    landManagementNonCo2EmissionsLabel: "Land mgmt non-CO₂",
    landUseChangeEmissionsLabel: "Land use change",
    nonFlagEmissionsLabel: "Non-FLAG emissions",
    sourceNameLabel: "Source / supplier name",
    sourceNamePlaceholder: "e.g. EPD International, Ecoinvent v3.10",
    sourceUrlLabel: "Source URL (optional)",
    sourceUrlPlaceholder: "https://",
    isSupplierSpecificLabel: "This is a supplier-specific emission factor",
    saveErrorToast: "Failed to create emission factor",
    productSectionTitle: "Product information (optional)",
    productSectionDescription: "Provide if available. Can be used for analysis or categorisation of your data.",
    productNameLabel: "Product name",
    productNamePlaceholder: "e.g. Recycled aluminium ingot",
    productCategoryLabel: "Category",
    methodologyAndBreakdownSectionTitle: "Emission factor details (optional)",
    methodologyAndBreakdownSectionDescription:
      "Optional data from the emission factor, such as scope, methodology, and standards. Enter all amounts per declared unit.",
    methodologyAndBreakdownFilledCount: (filled: number, total: number): ReactElement => (
      <>
        {renderVariable(filled)} of {renderVariable(total)} filled
      </>
    ),
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    formWidthLabel: "Form width",
    formWidthStandard: "Standard",
    formWidthWide: "Wide",
    yesOption: "Yes",
    noOption: "No",
  },
};
